import { Clipboard } from '@capacitor/clipboard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Card, IconButton, List, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { BikeleasingV2EmployerContributionDTOTypeEnum } from 'probonio-shared-ui/api';
import { useWithMessage } from 'probonio-shared-ui/module/error';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ValueListItem } from '../../component/list';
import { useAppSelector } from '../../redux/hooks';
import { useBikeleasingCondition } from './useBikeleasingDetails';

const ClipboardCopyItem: React.FC<{ value: string }> = ({ value }: { value: string }) => {
  const { t } = useTranslation('benefitModule');
  const { enqueueSnackbar } = useSnackbar();
  const withMessage = useWithMessage();
  const copyToClipboard = useCallback(() => {
    Clipboard.write({ string: value })
      .then(() => enqueueSnackbar(t('extensions:bikeleasing_v2_benefit.condition.copied'), { variant: 'success' }))
      .catch(withMessage);
  }, [enqueueSnackbar, t, value, withMessage]);
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography
        variant="inherit"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
      <IconButton sx={{ marginLeft: 0.5, p: 0 }} onClick={copyToClipboard}>
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

export const BikeleasingOverview: React.FC = () => {
  const { t } = useTranslation('benefitModule');

  const bikeleasingCondition = useBikeleasingCondition();

  const employeeNumber = useAppSelector(state => state.me.employment?.employeeNumber);
  const employeeCode = useAppSelector(state => state.me.employment?.bikeleasingEmployeeCode);
  return (
    <>
      <Box sx={{ marginBottom: 1, marginTop: 1 }}>
        <Typography variant="h5">{t('extensions:bikeleasing_v2_benefit.information')}</Typography>
      </Box>
      <Card elevation={0} sx={{ maxWidth: '100%', marginBottom: 1, flexShrink: 0 }}>
        <List>
          {bikeleasingCondition?.companyCode && (
            <ValueListItem
              label={t('extensions:bikeleasing_v2_benefit.condition.companyCode')}
              value={<ClipboardCopyItem value={bikeleasingCondition.companyCode} />}
              dataTestId="bikeleasing-company-code"
            />
          )}

          {employeeCode && (
            <ValueListItem
              label={t('extensions:bikeleasing_v2_benefit.userName')}
              value={<ClipboardCopyItem value={employeeCode} />}
              dataTestId="bikeleasing-employee-code"
            />
          )}
          {employeeNumber && (
            <ValueListItem
              label={t('extensions:bikeleasing_v2_benefit.employeeNumber')}
              value={<ClipboardCopyItem value={employeeNumber} />}
            />
          )}
        </List>
      </Card>
      {bikeleasingCondition?.allowedBikeTypes && (
        <>
          <Box sx={{ marginBottom: 1, marginTop: 0.5 }}>
            <Typography variant="h5">{t('extensions:bikeleasing_v2_benefit.conditions')}</Typography>
          </Box>
          <Card elevation={0} sx={{ maxWidth: '100%', marginBottom: 1, flexShrink: 0 }}>
            <List>
              <ValueListItem
                label={t('extensions:bikeleasing_v2_benefit.condition.bikeTypes')}
                value={bikeleasingCondition.allowedBikeTypes.join(', ')}
                subLabel={t('extensions:bikeleasing_v2_benefit.condition.availableTypes')}
                multiLine={bikeleasingCondition.allowedBikeTypes.length > 2}
              />
              <ValueListItem
                label={t('extensions:bikeleasing_v2_benefit.condition.maxBikesPerEmployee')}
                value={bikeleasingCondition.maxNumberOfBikesPerEmployee}
              />
              <ValueListItem
                label={t('extensions:bikeleasing_v2_benefit.condition.leasingDurationInMonths')}
                subLabel={t('extensions:bikeleasing_v2_benefit.condition.inMonths')}
                value={bikeleasingCondition.leasingDurationInMonths}
              />
              {bikeleasingCondition.employerContribution?.type === BikeleasingV2EmployerContributionDTOTypeEnum.Absolute && (
                <ValueListItem
                  label={t('extensions:bikeleasing_v2_benefit.condition.conditionGrantAmount')}
                  subLabel={t('extensions:bikeleasing_v2_benefit.condition.byEmployer')}
                  value={t('common:money', { money: bikeleasingCondition.employerContribution?.amount })}
                />
              )}
              {bikeleasingCondition.employerContribution?.type === BikeleasingV2EmployerContributionDTOTypeEnum.Percentage && (
                <ValueListItem
                  label={t('extensions:bikeleasing_v2_benefit.condition.conditionGrantAmount')}
                  subLabel={t('extensions:bikeleasing_v2_benefit.condition.byEmployer')}
                  value={`${bikeleasingCondition.employerContribution?.percentage}%`}
                />
              )}
              {!!bikeleasingCondition.priceRestrictions?.maxTotalPurchasePriceForAllBikes?.grossAmount && (
                <ValueListItem
                  label={t('extensions:bikeleasing_v2_benefit.condition.maxAmountPerEmployee')}
                  subLabel={t('extensions:bikeleasing_v2_benefit.condition.total')}
                  value={t('common:money', {
                    money: bikeleasingCondition.priceRestrictions?.maxTotalPurchasePriceForAllBikes.grossAmount,
                  })}
                />
              )}
              {!!bikeleasingCondition.priceRestrictions?.maxPurchasePrice?.grossAmount && (
                <ValueListItem
                  label={t('extensions:bikeleasing_v2_benefit.condition.conditionMaximumBikePrice')}
                  subLabel={t('extensions:bikeleasing_v2_benefit.condition.perBike')}
                  value={t('common:money', { money: bikeleasingCondition.priceRestrictions?.maxPurchasePrice.grossAmount })}
                />
              )}
              {!!bikeleasingCondition.priceRestrictions?.minPurchasePrice?.grossAmount && (
                <ValueListItem
                  label={t('extensions:bikeleasing_v2_benefit.condition.conditionMinimumBikePrice')}
                  subLabel={t('extensions:bikeleasing_v2_benefit.condition.perBike')}
                  value={t('common:money', { money: bikeleasingCondition.priceRestrictions?.minPurchasePrice.grossAmount })}
                />
              )}
            </List>
          </Card>
        </>
      )}
    </>
  );
};
